import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, Typography, Grid, Button, Chip, ToggleButtonGroup, ToggleButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, TextField
} from "@mui/material";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import useAuth from "../hooks/useAuth";

const API = process.env.REACT_APP_API_URL || "";

// Función para calcular rango de fechas según periodo
function calcularRango(periodo) {
  const hoy = new Date();
  let desde = new Date();
  
  if (periodo === "dia") { desde = new Date(hoy); }
  else if (periodo === "semana") {
    desde = new Date(hoy);
    const dia = desde.getDay();
    const diffLunes = dia === 0 ? 6 : dia - 1;
    desde.setDate(hoy.getDate() - diffLunes);
  }
  else if (periodo === "mes") { desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1); }
  else if (periodo === "anio") { desde = new Date(hoy.getFullYear(), 0, 1); }
  
  return {
    desde: desde.toISOString().split("T")[0],
    hasta: hoy.toISOString().split("T")[0]
  };
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Si es CAJERO, mostrar pantalla de bienvenida en vez del dashboard completo
  if (user?.role === "CAJERO") {
    const hora = new Date().getHours();
    const saludo = hora < 12 ? "Buenos dias" : hora < 18 ? "Buenas tardes" : "Buenas noches";
    const nombre = user?.nombre || user?.email?.split("@")[0] || "Vendedor";

    return (
      <Box sx={{ p: 4, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <Paper sx={{ p: 5, textAlign: "center", maxWidth: 500, width: "100%" }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {saludo}, {nombre}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Selecciona una opcion del menu para comenzar
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
            <Button variant="contained" size="large" fullWidth sx={{ maxWidth: 300, py: 1.5 }}
              onClick={() => navigate("/pos")}>
              Punto de Venta (POS)
            </Button>
            <Button variant="outlined" size="large" fullWidth sx={{ maxWidth: 300, py: 1.5 }}
              onClick={() => navigate("/crear-factura")}>
              Crear Factura
            </Button>
            <Button variant="outlined" size="large" fullWidth sx={{ maxWidth: 300, py: 1.5 }}
              onClick={() => navigate("/facturaslist")}>
              Ver Facturas
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  const [periodo, setPeriodo] = useState("mes");
  const [fechaDesde, setFechaDesde] = useState(() => calcularRango("mes").desde);
  const [fechaHasta, setFechaHasta] = useState(() => calcularRango("mes").hasta);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    ventas: 0, compras: 0, resultado: 0, porCobrar: 0, porPagar: 0,
    facturas: [], comprasLista: [], grafico: [], actividad: [], topProductos: []
  });

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (periodo !== "personalizado") {
      const rango = calcularRango(periodo);
      setFechaDesde(rango.desde);
      setFechaHasta(rango.hasta);
    }
  }, [periodo]);

  useEffect(() => {
    if (fechaDesde && fechaHasta) cargarDatos(fechaDesde, fechaHasta);
  }, [fechaDesde, fechaHasta]);

  const cargarDatos = async (desde, hasta) => {
    setLoading(true);
    try {
      // Cargar facturas
      const facRes = await fetch(`${API}/api/facturas`, { headers });
      const facturas = await facRes.json();
      const facturasArr = Array.isArray(facturas) ? facturas : [];
      
      // Filtrar por fecha (soportar múltiples formatos) y excluir anuladas
      const facFiltradas = facturasArr.filter(f => {
        // Excluir facturas anuladas
        const estado = (f.estadoSRI || f.estado_sri || f.estado || "").toUpperCase();
        if (estado === "ANULADA" || estado === "ANULADO") return false;

        // Intentar obtener fecha de cualquier campo disponible
        const fechaRaw = f.fechaEmision || f.fechaRegistro || f.fecha || "";
        // Puede ser "27/05/2026" o "2026-05-27" o "2026-05-27T..."
        let fecha = "";
        if (fechaRaw.includes("/")) {
          // Formato dd/mm/yyyy → convertir a yyyy-mm-dd
          const parts = fechaRaw.split("/");
          if (parts.length === 3) fecha = `${parts[2]}-${parts[1]}-${parts[0]}`;
        } else {
          fecha = fechaRaw.substring(0, 10);
        }
        if (!fecha || fecha.length < 10) return true;
        return fecha >= desde && fecha <= hasta;
      });

      const totalVentas = facFiltradas.reduce((s, f) => s + parseFloat(f.totales?.valorPagar || f.totales?.importeTotal || 0), 0);

      // Cargar compras
      let totalCompras = 0;
      let comprasLista = [];
      try {
        // Intentar endpoint de compras dinámico primero
        let comprasArr = [];
        try {
          const compRes = await fetch(`${API}/api/contabilidad/compras`, { headers });
          if (compRes.ok) {
            const compras = await compRes.json();
            comprasArr = Array.isArray(compras) ? compras : [];
          }
        } catch {}
        
        // Si no hay, intentar con compras del modelo global
        if (comprasArr.length === 0) {
          try {
            const compRes2 = await fetch(`${API}/api/cuentas-pagar/compras-pendientes`, { headers });
            if (compRes2.ok) {
              const data2 = await compRes2.json();
              comprasArr = Array.isArray(data2?.data || data2) ? (data2?.data || data2) : [];
            }
          } catch {}
        }

        const compFiltradas = comprasArr.filter(c => {
          const fecha = (c.fecha_emision || c.fecha_factura || c.fecha || c.created_at || "").substring(0, 10);
          if (!fecha) return true;
          return fecha >= desde && fecha <= hasta;
        });
        totalCompras = compFiltradas.reduce((s, c) => s + parseFloat(c.totales?.importeTotal || c.total || c.total_a_pagar || c.subtotal || 0), 0);
        comprasLista = compFiltradas.slice(0, 5);
      } catch {}

      // Cuentas por cobrar/pagar
      let porCobrar = 0, porPagar = 0;
      try {
        const cxcRes = await fetch(`${API}/api/cuentas-cobrar/resumen-cartera`, { headers });
        if (cxcRes.ok) {
          const cxc = await cxcRes.json();
          const cxcData = cxc.data || cxc;
          porCobrar = parseFloat(cxcData.saldo_pendiente || cxcData.total_pendiente || 0);
        }
      } catch {}
      try {
        const cxpRes = await fetch(`${API}/api/cuentas-pagar/resumen-obligaciones`, { headers });
        if (cxpRes.ok) {
          const cxp = await cxpRes.json();
          const cxpData = cxp.data || cxp;
          porPagar = parseFloat(cxpData.saldo_pendiente || cxpData.total_pendiente || 0);
        }
      } catch {}

      // Gráfico mensual
      const grafico = generarGraficoMensual(facturasArr);

      // Actividad reciente
      const actividad = [
        { texto: `${facFiltradas.length} facturas emitidas`, tipo: "info" },
        { texto: `${comprasLista.length} compras registradas`, tipo: "info" },
      ];
      if (porCobrar > 0) actividad.push({ texto: `$${porCobrar.toFixed(2)} pendiente de cobro`, tipo: "warning" });
      if (porPagar > 0) actividad.push({ texto: `$${porPagar.toFixed(2)} pendiente de pago`, tipo: "warning" });

      setData({
        ventas: totalVentas,
        compras: totalCompras,
        resultado: totalVentas - totalCompras,
        porCobrar,
        porPagar,
        facturas: facFiltradas.slice(0, 10),
        comprasLista,
        grafico,
        actividad,
        topProductos: []
      });
    } catch (err) {
      console.error("Error cargando dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const generarGraficoMensual = (facturas) => {
    const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    const anio = new Date().getFullYear();
    // Excluir anuladas del gráfico
    const facturasValidas = facturas.filter(f => {
      const estado = (f.estadoSRI || f.estado_sri || f.estado || "").toUpperCase();
      return estado !== "ANULADA" && estado !== "ANULADO";
    });
    return meses.map((mes, i) => {
      const ventas = facturasValidas.filter(f => {
        const fechaRaw = f.fechaEmision || f.fechaRegistro || "";
        let d;
        if (fechaRaw.includes("/")) {
          const parts = fechaRaw.split("/");
          d = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        } else {
          d = new Date(fechaRaw);
        }
        return d.getFullYear() === anio && d.getMonth() === i;
      }).reduce((s, f) => s + parseFloat(f.totales?.valorPagar || 0), 0);
      return { mes, ventas: parseFloat(ventas.toFixed(2)) };
    });
  };

  const kpiCard = (label, value, color, icon) => (
    <Paper sx={{ p: 2, textAlign: "center", borderLeft: `4px solid ${color}`, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>
        {icon} {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ color, mt: 0.5 }}>
        ${value.toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </Typography>
    </Paper>
  );

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: "100%" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h6" fontWeight={700}>Panel de Control</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" size="small" onClick={() => navigate("/crear-factura")}
            sx={{ bgcolor: "#1a237e", textTransform: "none", fontSize: 12 }}>+ Facturar</Button>
          <Button variant="outlined" size="small" onClick={() => navigate("/compras")}
            sx={{ textTransform: "none", fontSize: 12 }}>+ Compra</Button>
        </Box>
      </Box>

      {/* Filtros de período */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <ToggleButtonGroup value={periodo} exclusive onChange={(_, v) => v && setPeriodo(v)} size="small">
          <ToggleButton value="dia" sx={{ px: 1.5, fontSize: 12 }}>Día</ToggleButton>
          <ToggleButton value="semana" sx={{ px: 1.5, fontSize: 12 }}>Semana</ToggleButton>
          <ToggleButton value="mes" sx={{ px: 1.5, fontSize: 12 }}>Mes</ToggleButton>
          <ToggleButton value="anio" sx={{ px: 1.5, fontSize: 12 }}>Año</ToggleButton>
          <ToggleButton value="personalizado" sx={{ px: 1.5, fontSize: 12 }}>Personalizado</ToggleButton>
        </ToggleButtonGroup>
        {periodo === "personalizado" && (
          <>
            <TextField size="small" type="date" value={fechaDesde}
              onChange={e => setFechaDesde(e.target.value)} sx={{ width: 140 }} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" value={fechaHasta}
              onChange={e => setFechaHasta(e.target.value)} sx={{ width: 140 }} InputLabelProps={{ shrink: true }} />
          </>
        )}
        <Chip label={`${fechaDesde} → ${fechaHasta}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
      </Box>

      {/* KPIs */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md>{kpiCard("Ventas", data.ventas, "#2e7d32", "💰")}</Grid>
        <Grid item xs={6} sm={4} md>{kpiCard("Compras", data.compras, "#c62828", "🛒")}</Grid>
        <Grid item xs={6} sm={4} md>{kpiCard("Resultado", data.resultado, "#1565c0", "📈")}</Grid>
        <Grid item xs={6} sm={6} md>{kpiCard("Por Cobrar", data.porCobrar, "#f57c00", "⏳")}</Grid>
        <Grid item xs={12} sm={6} md>{kpiCard("Por Pagar", data.porPagar, "#7b1fa2", "⏳")}</Grid>
      </Grid>

      {/* Contenido principal */}
      <Grid container spacing={1.5}>
        {/* Columna izquierda */}
        <Grid item xs={12} lg={8}>
          {/* Gráfico */}
          <Paper sx={{ p: 1.5, mb: 1.5 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              📊 Ventas por Mes ({new Date().getFullYear()})
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data.grafico}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                <Legend />
                <Line type="monotone" dataKey="ventas" stroke="#2e7d32" strokeWidth={2} name="Ventas" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          {/* Últimas Facturas */}
          <Paper sx={{ p: 1.5, mb: 1.5 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              🧾 Últimas Facturas
            </Typography>
            {data.facturas.length === 0 ? (
              <Typography color="text.secondary" variant="body2">No hay facturas en este período</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Cliente</strong></TableCell>
                      <TableCell><strong>Número</strong></TableCell>
                      <TableCell align="right"><strong>Monto</strong></TableCell>
                      <TableCell><strong>Fecha</strong></TableCell>
                      <TableCell align="center"><strong>Estado</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.facturas.map((f, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{f.cliente?.nombre || "—"}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {f.secuencial ? String(f.secuencial).padStart(9, "0") : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          ${parseFloat(f.totales?.valorPagar || 0).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{(f.fechaEmision || "").substring(0, 10)}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={f.estadoSRI || "Pendiente"}
                            color={f.estadoSRI === "AUTORIZADO" ? "success" : "warning"} variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Últimas Compras */}
          <Paper sx={{ p: 1.5 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              📦 Últimas Compras
            </Typography>
            {data.comprasLista.length === 0 ? (
              <Typography color="text.secondary" variant="body2">No hay compras en este período</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Proveedor</strong></TableCell>
                      <TableCell align="right"><strong>Monto</strong></TableCell>
                      <TableCell><strong>Fecha</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.comprasLista.map((c, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{c.proveedor?.razonSocial || c.proveedor?.razon_social || "—"}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          ${parseFloat(c.totales?.importeTotal || c.total || 0).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{(c.fecha_emision || "").substring(0, 10)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Columna derecha - Actividad */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 1.5, mb: 1.5 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              🔔 Actividad Reciente
            </Typography>
            {data.actividad.map((a, i) => (
              <Box key={i} sx={{ py: 1, borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: a.tipo === "warning" ? "#f57c00" : "#4caf50" }} />
                <Typography variant="body2">{a.texto}</Typography>
              </Box>
            ))}
          </Paper>

          <Paper sx={{ p: 1.5, mb: 1.5 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              📋 Resumen Financiero
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px solid #f0f0f0" }}>
              <Typography variant="body2">Pendiente de Cobro</Typography>
              <Typography variant="body2" fontWeight={600} color="warning.main">
                ${data.porCobrar.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px solid #f0f0f0" }}>
              <Typography variant="body2">Pendiente de Pago</Typography>
              <Typography variant="body2" fontWeight={600} color="error.main">
                ${data.porPagar.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
              <Typography variant="body2" fontWeight={600}>Flujo Neto</Typography>
              <Typography variant="body2" fontWeight={700} color={data.resultado >= 0 ? "success.main" : "error.main"}>
                ${data.resultado.toFixed(2)}
              </Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 1.5 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              ⚡ Accesos Rápidos
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Button fullWidth variant="outlined" size="small" onClick={() => navigate("/crear-factura")}
                sx={{ justifyContent: "flex-start", textTransform: "none" }}>🧾 Nueva Factura</Button>
              <Button fullWidth variant="outlined" size="small" onClick={() => navigate("/compras")}
                sx={{ justifyContent: "flex-start", textTransform: "none" }}>🛒 Nueva Compra</Button>
              <Button fullWidth variant="outlined" size="small" onClick={() => navigate("/clientes")}
                sx={{ justifyContent: "flex-start", textTransform: "none" }}>👥 Clientes</Button>
              <Button fullWidth variant="outlined" size="small" onClick={() => navigate("/contabilidad/reportes")}
                sx={{ justifyContent: "flex-start", textTransform: "none" }}>📊 Reportes</Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
